/**
 * AI 読書アシストの「位置キー」まわりの計算
 *
 * 位置キーはバックエンド（util/book_text.py）と同じ物差し。
 * - EPUB: spine 番号 + その中の進み具合（0〜1）。例: 3 番目の 25% 地点 → 3.25
 * - PDF: 0 始まりのページ番号
 */
import type { Locator } from '@readium/shared'

import type { BookAiRelation, BookAiSection, BookAiTerm, BookAiTermOccurrences } from '@/api/services/book-ai'
import type { SkimLevel } from '@/store/useUISettingsStore'

// 進み具合が 1 になると次の spine 番号と区別できないので、わずかに手前で止める
const MAX_PROGRESSION = 0.9999

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Readium の href（絶対 URL・res/ 付き相対 URL）を spine の href と比べられる形にする */
export const normalizeSpineHref = (href: string): string => {
  const path = safeDecode(href.split('#')[0].split('?')[0])
  const resIndex = path.lastIndexOf('/res/')
  if (resIndex >= 0) return path.slice(resIndex + '/res/'.length)
  return path.startsWith('res/') ? path.slice('res/'.length) : path
}

export const findSpineIndex = (spine: string[], href: string): number => {
  const target = normalizeSpineHref(href)
  return spine.findIndex((s) => s === target || target.endsWith(`/${s}`) || s.endsWith(`/${target}`))
}

/** spine ごとの開始位置（本全体に対する進み具合 totalProgression） */
export const buildSpineStarts = (spine: string[], positions: Locator[]): number[] | null => {
  if (spine.length === 0 || positions.length === 0) return null
  const starts = new Map<number, number>()
  for (const position of positions) {
    const index = findSpineIndex(spine, position.href)
    const total = position.locations.totalProgression
    if (index < 0 || total == null || starts.has(index)) continue
    starts.set(index, total)
  }
  // 位置の無い spine（画像だけのページなど）は直前の値で埋める
  let last = 0
  return spine.map((_, index) => {
    last = starts.get(index) ?? last
    return last
  })
}

const keyFromTotalProgression = (spineStarts: number[], total: number): number => {
  let index = 0
  spineStarts.forEach((start, i) => {
    if (start <= total) index = i
  })
  const start = spineStarts.at(index) ?? 0
  const end = spineStarts.at(index + 1) ?? 1
  const progression = end > start ? (total - start) / (end - start) : 0
  return index + Math.min(Math.max(progression, 0), MAX_PROGRESSION)
}

/**
 * EPUB の Locator を位置キーにする
 *
 * ページ送り表示は href + 章内の進み具合が正確なのでそれを使う。
 * 連続スクロール表示は href が概算で章内の進み具合も無いため、本全体の進み具合から逆算する。
 */
export const epubKeyFromLocator = (locator: Locator, spine: string[], spineStarts: number[] | null): number | null => {
  const index = findSpineIndex(spine, locator.href)
  const progression = locator.locations.progression
  if (index >= 0 && progression != null) return index + Math.min(progression, MAX_PROGRESSION)
  const total = locator.locations.totalProgression
  if (total != null && spineStarts) return keyFromTotalProgression(spineStarts, total)
  return index >= 0 ? index : null
}

/** 位置キーを、本全体に対する進み具合（0〜1）にする（シークバーの印の位置。spine の開始位置が不明なら null） */
export const totalProgressionFromKey = (spineStarts: number[] | null, key: number): number | null => {
  if (!spineStarts || spineStarts.length === 0) return null
  const index = Math.min(Math.max(Math.floor(key), 0), spineStarts.length - 1)
  const start = spineStarts.at(index) ?? 0
  const end = spineStarts.at(index + 1) ?? 1
  const progression = Math.min(Math.max(key - index, 0), 1)
  return Math.min(Math.max(start + progression * (end - start), 0), 1)
}

/** 本全体の進み具合（0〜1）の手前で最も近い位置（シークバーで移動する先）。位置が無ければ undefined */
export const locatorAtTotalProgression = (positions: Locator[], total: number): Locator | undefined => {
  let found: Locator | undefined
  for (const position of positions) {
    const value = position.locations.totalProgression
    if (value == null) continue
    if (value <= total + 1e-9) found = position
    else if (!found) found = position
  }
  return found
}

/** 位置キーを EPUB の spine の href と章内の進み具合に戻す */
export const epubLocationFromKey = (spine: string[], key: number): { href: string; progression: number } | null => {
  const index = Math.floor(key)
  const href = spine.at(index)
  if (href == null) return null
  return { href, progression: key - index }
}

/** 位置キーが属する章の番号（どの章より前なら -1） */
export const findSectionIndex = (sections: BookAiSection[], key: number | null): number => {
  if (key == null) return -1
  let found = -1
  for (const section of sections) {
    if (section.start <= key + 1e-6) found = section.index
  }
  return found
}

/** AI の重要文。level は 1: 核心 / 2: 重要 / 3: 補足 */
export type KeySentence = { text: string; key: number; level: SkimLevel }

// 重要度を持たない古い解析結果は「重要」とみなす
const DEFAULT_LEVEL: SkimLevel = 2

const toLevel = (value: number | undefined): SkimLevel =>
  value === 1 || value === 2 || value === 3 ? value : DEFAULT_LEVEL

/** 重要度が maxLevel 以上（数字が maxLevel 以下）の重要文を集める */
export const collectKeySentences = (sections: BookAiSection[], maxLevel: SkimLevel = 3): KeySentence[] =>
  sections.flatMap((section) => {
    const analysis = section.analysis
    if (!analysis) return []
    return analysis.key_sentences
      .map((text, i) => ({
        text,
        key: analysis.key_sentence_keys.at(i) ?? section.start,
        level: toLevel(analysis.key_sentence_levels.at(i)),
      }))
      .filter((sentence) => sentence.level <= maxLevel)
  })

/**
 * 位置キーの前後 margin にかかる章の重要文だけを集める
 * （本文の照合は文の数に比例して重いので、表示中の近くに絞る。位置が不明なら空）
 */
export const collectKeySentencesNear = (
  sections: BookAiSection[],
  key: number | null,
  margin: number,
  maxLevel: SkimLevel,
): KeySentence[] => {
  if (key == null) return []
  return collectKeySentences(
    sections.filter((s) => s.start <= key + margin && s.end > key - margin),
    maxLevel,
  )
}

export type GlossaryEntry = {
  name: string
  kind: BookAiTerm['kind']
  /** 本文中での別の呼び方（全章分をまとめたもの） */
  aliases: string[]
  /** 登場した章ごとの説明（古い順） */
  notes: { sectionIndex: number; sectionTitle: string; description: string }[]
}

const normalizeTerm = (value: string) => value.replace(/\s+/g, '').toLowerCase()

/**
 * 選択した文字が人物・用語の名前と一致すれば、その項目を返す（今読んでいる章までの説明で）
 */
export const findGlossaryEntry = (
  sections: BookAiSection[],
  uptoIndex: number,
  selectedText: string,
): GlossaryEntry | null => {
  const target = normalizeTerm(selectedText)
  if (!target) return null
  return (
    collectGlossary(sections, uptoIndex).find(
      (entry) => normalizeTerm(entry.name) === target || entry.aliases.some((alias) => normalizeTerm(alias) === target),
    ) ?? null
  )
}

/** 人物・用語の名前から登場箇所を引く（名前の大文字・小文字と空白の違いは無視する） */
export const buildOccurrenceMap = (termIndex: BookAiTermOccurrences[]): Map<string, number[]> =>
  new Map(termIndex.map((item) => [normalizeTerm(item.name), item.keys]))

export const occurrencesOf = (map: Map<string, number[]>, name: string): number[] => map.get(normalizeTerm(name)) ?? []

/** 登場箇所を章ごとにまとめる（章の順） */
export const groupOccurrencesBySection = (
  sections: BookAiSection[],
  keys: number[],
): { section: BookAiSection; count: number; firstKey: number }[] => {
  const groups = new Map<number, { section: BookAiSection; count: number; firstKey: number }>()
  for (const key of keys) {
    const index = findSectionIndex(sections, key)
    const section = sections.find((s) => s.index === index)
    if (!section) continue
    const group = groups.get(index)
    if (group) group.count += 1
    else groups.set(index, { section, count: 1, firstKey: key })
  }
  return [...groups.values()].sort((a, b) => a.section.index - b.section.index)
}

/**
 * 指定した章までに分かった関係を集める（同じ 2 人の関係は新しい章のものを採る）
 */
export const collectRelations = (sections: BookAiSection[], uptoIndex: number): BookAiRelation[] => {
  const relations = new Map<string, BookAiRelation>()
  for (const section of sections) {
    if (section.index > uptoIndex || !section.analysis) continue
    for (const relation of section.analysis.relations ?? []) {
      const pair = [normalizeTerm(relation.source), normalizeTerm(relation.target)].sort().join('\u0000')
      relations.set(pair, relation)
    }
  }
  return [...relations.values()]
}

/** いちばん大きい「つながった人のまとまり」の人数（関係が 2 人ずつばらばらなら 2） */
export const largestRelationGroup = (relations: BookAiRelation[]): number => {
  const neighbors = new Map<string, string[]>()
  for (const r of relations) {
    neighbors.set(r.source, [...(neighbors.get(r.source) ?? []), r.target])
    neighbors.set(r.target, [...(neighbors.get(r.target) ?? []), r.source])
  }
  const visited = new Set<string>()
  let largest = 0
  for (const start of neighbors.keys()) {
    if (visited.has(start)) continue
    let size = 0
    const stack = [start]
    visited.add(start)
    while (stack.length > 0) {
      const name = stack.pop() ?? ''
      size += 1
      for (const next of neighbors.get(name) ?? []) {
        if (visited.has(next)) continue
        visited.add(next)
        stack.push(next)
      }
    }
    largest = Math.max(largest, size)
  }
  return largest
}

/** その人物・用語（名前か別名）が関わる関係 */
export const relationsOf = (relations: BookAiRelation[], entry: Pick<GlossaryEntry, 'name' | 'aliases'>) => {
  const names = new Set([entry.name, ...entry.aliases].map(normalizeTerm))
  return relations
    .filter((r) => names.has(normalizeTerm(r.source)) || names.has(normalizeTerm(r.target)))
    .map((r) => ({ other: names.has(normalizeTerm(r.source)) ? r.target : r.source, relation: r.relation }))
}

/** 本全体（すべての章）を対象にするときの章の番号 */
export const ALL_SECTIONS = Number.MAX_SAFE_INTEGER

/**
 * 指定した章までに登場した人物・用語を集める（ネタバレ防止のため、先の章の説明は含めない）
 */
export const collectGlossary = (sections: BookAiSection[], uptoIndex: number): GlossaryEntry[] => {
  const entries = new Map<string, GlossaryEntry>()
  for (const section of sections) {
    if (section.index > uptoIndex || !section.analysis) continue
    for (const term of section.analysis.terms) {
      const id = term.name.trim().toLowerCase()
      if (!id) continue
      const entry = entries.get(id) ?? { name: term.name, kind: term.kind, aliases: [], notes: [] }
      for (const alias of term.aliases ?? []) {
        if (!entry.aliases.includes(alias)) entry.aliases.push(alias)
      }
      if (!entry.notes.some((note) => note.description === term.description)) {
        entry.notes.push({ sectionIndex: section.index, sectionTitle: section.title, description: term.description })
      }
      entries.set(id, entry)
    }
  }
  return [...entries.values()]
}
